import { convertLocalTimeToUTC, getBrowserTimezone } from './timezoneData';

export interface RepeatingScheduleFormValues {
  frequency: number;
  frequencyUnit: string;
  startingAt: { hour: number; minute: number } | null;
  timeZone: string;
}

export interface RepeatingScheduleApiPayload {
  frequency: number;
  frequencyUnit: string;
  startingAt?: string;
  timeZone?: string;
}

export interface RepeatingSchedulePayloadOptions {
  /**
   * Existing dataProtect rows: convert startingAt from browser-local wall clock,
   * omit timeZone so backend keeps the UTC-only StartDate path (roll-forward only).
   */
  legacyBrowserLocal?: boolean;
  browserTimeZone?: string;
}

/**
 * Build repeatingSchedule API payload.
 *
 * DST path (new/edited schedules): startingAt + timeZone paired — mirrors daily schedule.
 * Legacy path (existing rows without stored timeZone): startingAt only, browser-local conversion.
 */
export function buildRepeatingSchedulePayload(
  repeating: RepeatingScheduleFormValues,
  refDate: Date = new Date(),
  options?: RepeatingSchedulePayloadOptions
): RepeatingScheduleApiPayload {
  const payload: RepeatingScheduleApiPayload = {
    frequency: repeating.frequency,
    frequencyUnit: repeating.frequencyUnit,
  };

  if (!repeating.startingAt) {
    return payload;
  }

  if (options?.legacyBrowserLocal) {
    const browserTz = options.browserTimeZone ?? getBrowserTimezone();
    const utc = convertLocalTimeToUTC(
      repeating.startingAt.hour,
      repeating.startingAt.minute,
      browserTz,
      refDate
    );
    payload.startingAt = `${utc.hour.toString().padStart(2, '0')}:${utc.minute.toString().padStart(2, '0')}`;
    return payload;
  }

  const tz = repeating.timeZone.trim();
  if (!tz) {
    return payload;
  }

  const utc = convertLocalTimeToUTC(
    repeating.startingAt.hour,
    repeating.startingAt.minute,
    tz,
    refDate
  );
  payload.startingAt = `${utc.hour.toString().padStart(2, '0')}:${utc.minute.toString().padStart(2, '0')}`;
  payload.timeZone = tz;

  return payload;
}
